import * as React from "react";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridToolbarContainer,
  GridRowClassNameParams,
  GridToolbarExport,
} from "@mui/x-data-grid";
import DataGridCompat from "../../components/global/DataGridCompat";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { Customer } from "../../types/customer";
import { useNavigate } from "react-router-dom";
import ClientDrawer from "./ClientDrawer";
import {
  useDeleteCustomerMutation,
  useGetCustomersQuery,
} from "../../services/customer.service";
import { useCompany } from "../../hooks/useCompany";
import { useUILabel } from "../../hooks/useUILabel";

const TableToolbar = ({
  onSearch,
  onCreate,
}: {
  onSearch: (query?: string) => void;
  onCreate: () => void;
}) => {
  const enterToSearchLabel = useUILabel("common.enterToSearch");
  const resetLabel = useUILabel("common.reset");
  const addClientLabel = useUILabel("client.addClient");
  const clientsLabel = useUILabel("common.clients");
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    onSearch(debounceQuery);
  }, [debounceQuery]);

  return (
    <GridToolbarContainer
      style={{ paddingLeft: 10, paddingRight: 10, marginBottom: 5 }}
    >
      <TextField
        margin="dense"
        label={enterToSearchLabel}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {!!query && (
        <Button variant="text" sx={{ mt: 1 }} onClick={() => setQuery("")}>
          {resetLabel}
        </Button>
      )}
      <Stack style={{ flex: 1 }} direction="row" justifyContent="flex-end">
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{ allColumns: true, fileName: clientsLabel }}
        />
        <Button variant="outlined" onClick={onCreate} sx={{ ml: 2 }}>
          <AddIcon /> {addClientLabel}
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
};

const ClientsPage = () => {
  const clientsLabel = useUILabel("common.clients");
  const clientNameLabel = useUILabel("client.clientName");
  const phoneNumberLabel = useUILabel("common.phoneNumber");
  const emailLabel = useUILabel("common.email");
  const addressLabel = useUILabel("common.address");
  const editLabel = useUILabel("common.edit");
  const deleteLabel = useUILabel("common.delete");
  const cityLabel = useUILabel("common.city");
  const stateLabel = useUILabel("common.state");
  const zipLabel = useUILabel("common.zip");
  const successfullyDeletedLabel = useUILabel("common.successfullyDeleted");
  const deleteClientQuestionLabel = useUILabel("client.deleteClientQuestion");
  useTitle(clientsLabel);
  const navigate = useNavigate();
  const company = useCompany();

  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<Customer> | null;
  }>({
    open: false,
    row: null,
  });

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Customer | null>(null);

  const { data, isLoading } = useGetCustomersQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete] = useDeleteCustomerMutation();

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "client_name",
        headerName: clientNameLabel,
        minWidth: 200,
        flex: 1,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setCurrentItem(params.row);
              setOpen(true);
            }}
          >
            {params.row.first_name}
          </Link>
        ),
        disableExport: true,
      },
      {
        field: "first_name",
        headerName: clientNameLabel,
        hide: true,
      },
      {
        field: "phone_number",
        headerName: phoneNumberLabel,
        headerAlign: "center",
        minWidth: 150,
        sortable: true,
        filterable: true,
        align: "center",
      },
      {
        field: "email",
        headerName: emailLabel,
        hide: true,
      },
      {
        field: "physical_address_1",
        headerName: addressLabel,
        hide: true,
      },
      {
        field: "city",
        headerName: cityLabel,
        headerAlign: "center",
        minWidth: 150,
        sortable: true,
        filterable: true,
        align: "center",
      },
      {
        field: "state",
        headerName: stateLabel,
        headerAlign: "center",
        minWidth: 150,
        sortable: true,
        filterable: true,
        align: "center",
      },
      {
        field: "zip",
        headerName: zipLabel,
        hide: true,
      },
      {
        field: "control",
        headerName: `${editLabel}/${deleteLabel}`,
        headerAlign: "center",
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction={"row"}>
            <IconButton onClick={() => handleEditRow(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => handleDeleteRow(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
        disableExport: true,
      },
    ],
    [clientNameLabel, phoneNumberLabel, emailLabel, addressLabel, editLabel, deleteLabel, cityLabel, stateLabel, zipLabel]
  );

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const handleCreate = React.useCallback(() => {
    navigate(`/clients/create`);
  }, []);

  const handleDeleteRow = (row: GridRowModel<Customer>) => {
    setDeleteModal({ open: true, row });
  };
  const handleEditRow = (row: GridRowModel<Customer>) => {
    navigate(`/clients/${row.id}/edit`);
  };

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success(successfullyDeletedLabel);
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const CustomToolbar = React.useMemo(
    () => () =>
      <TableToolbar onSearch={handleSearch} onCreate={handleCreate} />,
    [handleSearch]
  );

  return (
    <Box sx={{ bgcolor: "background.paper", width: "auto" }}>
      <Paper style={{ height: "85vh" }}>
        <DataGridCompat
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page as any}
          onPageChange={(newPage: any) => setPage(newPage)}
          pageSize={pageSize as any}
          rowsPerPageOptions={[1000]}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading}
          components={{
            LoadingOverlay: LinearProgress,
            NoResultsOverlay: NoResultsOverlay,
            Toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          // rowHeight={42}
          density="compact"
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
              borderTop: "1px solid #ddd",
            },
          }}
        />
        <ConfirmDeleteModal
          open={deleteModal.open}
          onClose={closeDeleteModal}
          onDelete={handleDelete}
          message={deleteClientQuestionLabel.replace("{name}", deleteModal.row?.first_name || "")}
        />
      </Paper>

      <ClientDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </Box>
  );
};
export default ClientsPage;
